import React, { useCallback, useMemo } from 'react';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { useUIState } from '@client/store/ui';
import { Page } from '@client/store/ui/ui.model';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { PillTabs } from '@ui/PillTabs';
import { useUserIsAdmin } from '@client/hooks/use-user.hooks';
import { isEnvironment } from '@shared/helpers/environment.helpers';
import { useBuildingDesign } from '@features/design/hooks/design.hooks';
import { hasAnyCompletedStorey } from '@shared/helpers/design/building.helpers';

type NavTab = 'design' | 'build' | 'report';

const DEFAULT_TABS: readonly NavTab[] = ['design', 'build', 'report'];
const ADMIN_TABS: readonly NavTab[] = [...DEFAULT_TABS];
const TEST_ENV_TABS: readonly NavTab[] = [...ADMIN_TABS];

const TAB_TO_PAGE: Record<NavTab, Page> = {
  design: Page.Design,
  build: Page.Elements,
  report: Page.Report,
};

const pageToTab = (page: Page): NavTab => {
  switch (page) {
    case Page.Design:
    case Page.Diff:
    case Page.Calculations:
    case Page.DataTables:
      return 'design';
    case Page.Elements:
    case Page.Products:
    case Page.Import:
      return 'build';
    case Page.Report:
      return 'report';
  }
};

const PageNavigation: React.FC = () => {
  const { selectedPage } = useUIState('selectedPage');
  const navigateTo = useProjectNavigation();
  const userIsAdmin = useUserIsAdmin();
  const design = useBuildingDesign();
  const showPageNavigation = hasAnyCompletedStorey(design.buildings);

  const tabs = userIsAdmin
    ? isEnvironment('production')
      ? ADMIN_TABS
      : TEST_ENV_TABS
    : DEFAULT_TABS;

  const activeTab = useMemo(() => pageToTab(selectedPage), [selectedPage]);

  const selectTab = useCallback(
    (tab: NavTab) =>
      navigateTo({ page: TAB_TO_PAGE[tab], elementId: URL_CATEGORY_NONE }),
    [navigateTo],
  );

  if (!showPageNavigation) return null;

  return (
    <PillTabs tabs={tabs} value={activeTab} onChange={selectTab} size="large" />
  );
};

export default PageNavigation;
